import React, { useEffect, useRef } from 'react'
import { Calendar, CircleAlert } from 'lucide-react'
import { formatDate } from '../../helpers'
import colors from 'colors'

interface IDateTimePickerProps {
  // Date
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
  showCalendar: boolean
  onShowCalendarChange: (show: boolean) => void
  currentCalendarDate: Date
  onCurrentCalendarDateChange: (date: Date) => void

  // Start Time
  startTime: string
  onStartTimeSelect: (time: string) => void
  showStartTimePicker: boolean
  onShowStartTimePickerChange: (show: boolean) => void
  displayedStartTime: string
  validStartTimes: { value: string; display: string }[]

  // End Time
  endTime: string
  onEndTimeSelect: (time: string) => void
  showEndTimePicker: boolean
  onShowEndTimePickerChange: (show: boolean) => void
  displayedEndTime: string
  validEndTimes: { value: string; display: string }[]

  // Refs
  calendarRef: React.RefObject<HTMLDivElement>
  startTimeRef: React.RefObject<HTMLDivElement>
  endTimeRef: React.RefObject<HTMLDivElement>

  // Validation errors
  dateError: string
  timeError: string
}

// Calendar Picker Component
const CalendarPicker: React.FC<{
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
  currentCalendarDate: Date
  onCurrentCalendarDateChange: (date: Date) => void
  calendarRef: React.RefObject<HTMLDivElement>
}> = ({
  selectedDate,
  onDateSelect,
  currentCalendarDate,
  onCurrentCalendarDateChange,
  calendarRef,
}) => {
  const today = new Date()
  const currentMonth = currentCalendarDate.getMonth()
  const currentYear = currentCalendarDate.getFullYear()

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay()

  const days: (Date | null)[] = []

  // Empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null)
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(currentYear, currentMonth, day))
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const goToPreviousMonth = () => {
    onCurrentCalendarDateChange(new Date(currentYear, currentMonth - 1, 1))
  }

  const goToNextMonth = () => {
    onCurrentCalendarDateChange(new Date(currentYear, currentMonth + 1, 1))
  }

  return (
    <div
      ref={calendarRef}
      className="absolute top-full left-0 mt-1 bg-white border border-primary-outline rounded-lg shadow-lg z-[110] p-4 w-80"
    >
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPreviousMonth}
          className="p-1 rounded-md hover:bg-primary-brandLight text-primary-text"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <h3 className="text-lg font-semibold text-primary-text">
          {monthNames[currentMonth]} {currentYear}
        </h3>

        <button
          onClick={goToNextMonth}
          className="p-1 rounded-md hover:bg-primary-brandLight text-primary-text"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-silver-light py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="h-8"></div>
          }

          const isToday = date.toDateString() === today.toDateString()
          const isPast = date < today && !isToday
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()

          return (
            <button
              key={index}
              onClick={() => !isPast && onDateSelect(date)}
              disabled={isPast}
              className={`
                h-8 w-8 text-sm rounded-md flex items-center justify-center
                ${
                  isPast
                    ? 'text-silver-light cursor-not-allowed opacity-40'
                    : 'text-primary-text hover:bg-primary-brandLight cursor-pointer'
                }
                ${isToday ? 'bg-primary text-white font-semibold' : ''}
                ${isSelected && !isToday ? 'bg-primary-brandLight border border-primary' : ''}
              `}
            >
              {date.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Time Picker Component
const TimePicker = React.forwardRef<
  HTMLDivElement,
  {
    timeSlots: { value: string; display: string }[]
    onTimeSelect: (time: string) => void
    selectedTime: string
  }
>(({ timeSlots, onTimeSelect, selectedTime }, ref) => (
  <div
    ref={ref}
    className="absolute top-full left-0 mt-1 bg-white border border-primary-outline rounded-lg shadow-lg z-[110] max-h-48 overflow-y-auto w-full"
  >
    {timeSlots.map((slot) => (
      <button
        key={slot.value}
        onClick={() => onTimeSelect(slot.value)}
        className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-brandLight ${
          selectedTime === slot.value ? 'bg-primary-brandLight text-primary' : 'text-primary-text'
        }`}
      >
        {slot.display}
      </button>
    ))}
  </div>
))

// Main DateTimePicker Component
export const DateTimePicker: React.FC<IDateTimePickerProps> = ({
  selectedDate,
  onDateSelect,
  showCalendar,
  onShowCalendarChange,
  currentCalendarDate,
  onCurrentCalendarDateChange,
  startTime,
  onStartTimeSelect,
  showStartTimePicker,
  onShowStartTimePickerChange,
  displayedStartTime,
  validStartTimes,
  endTime,
  onEndTimeSelect,
  showEndTimePicker,
  onShowEndTimePickerChange,
  displayedEndTime,
  validEndTimes,
  calendarRef,
  startTimeRef,
  endTimeRef,
  dateError,
  timeError,
}) => {
  // Refs for the input containers to detect clicks outside
  const datePickerContainerRef = useRef<HTMLDivElement>(null)
  const startTimeContainerRef = useRef<HTMLDivElement>(null)
  const endTimeContainerRef = useRef<HTMLDivElement>(null)

  // Handle click outside for all pickers
  useEffect(() => {
    if (!showCalendar && !showStartTimePicker && !showEndTimePicker) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Check calendar picker
      if (
        showCalendar &&
        calendarRef.current &&
        datePickerContainerRef.current &&
        !calendarRef.current.contains(target) &&
        !datePickerContainerRef.current.contains(target)
      ) {
        onShowCalendarChange(false)
      }

      // Check start time picker
      if (
        showStartTimePicker &&
        startTimeRef.current &&
        startTimeContainerRef.current &&
        !startTimeRef.current.contains(target) &&
        !startTimeContainerRef.current.contains(target)
      ) {
        onShowStartTimePickerChange(false)
      }

      // Check end time picker
      if (
        showEndTimePicker &&
        endTimeRef.current &&
        endTimeContainerRef.current &&
        !endTimeRef.current.contains(target) &&
        !endTimeContainerRef.current.contains(target)
      ) {
        onShowEndTimePickerChange(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [
    showCalendar,
    showStartTimePicker,
    showEndTimePicker,
    calendarRef,
    startTimeRef,
    endTimeRef,
    onShowCalendarChange,
    onShowStartTimePickerChange,
    onShowEndTimePickerChange,
  ])

  return (
    <>
      <div className="mb-5">
        <label className="block text-sm font-semibold text-primary-text mb-2">Date & Time*</label>
        <div className="flex items-center gap-3">
          {/* Date Picker */}
          <div className="flex-1 relative" ref={datePickerContainerRef}>
            <div
              className={`flex items-center border rounded-lg px-3 py-2 cursor-pointer shadow ${
                dateError ? 'border-alert' : 'border-primary-outline'
              }`}
              style={dateError ? { borderColor: colors.alert.DEFAULT } : {}}
              onClick={() => {
                onShowCalendarChange(!showCalendar)
                onShowStartTimePickerChange(false)
                onShowEndTimePickerChange(false)
              }}
            >
              <Calendar className="w-4 h-4 mr-2 opacity-60" />
              <span
                className={`flex-1 text-sm whitespace-nowrap ${
                  selectedDate ? 'text-primary-text' : 'text-silver-light opacity-60'
                }`}
              >
                {selectedDate ? formatDate(selectedDate) : 'Pick a date'}
              </span>
            </div>
            {showCalendar && (
              <CalendarPicker
                selectedDate={selectedDate}
                onDateSelect={onDateSelect}
                currentCalendarDate={currentCalendarDate}
                onCurrentCalendarDateChange={onCurrentCalendarDateChange}
                calendarRef={calendarRef}
              />
            )}
            {/* Date Error - positioned below date picker */}
            {dateError && (
              <p
                className="text-xs mt-1.5 flex items-center gap-1 font-medium absolute top-full left-0"
                style={{ color: colors.alert.DEFAULT }}
              >
                <CircleAlert className="w-3.5 h-3.5 flex-shrink-0" color={colors.alert.DEFAULT} />
                {dateError}
              </p>
            )}
          </div>

          <div className="w-[120px] relative" ref={startTimeContainerRef}>
            <div
              className={`w-full rounded-lg border px-3 py-2 text-sm text-center focus:border-primary focus:ring-primary focus:outline-none cursor-pointer shadow ${
                timeError ? 'border-alert' : 'border-primary-outline'
              }`}
              style={timeError ? { borderColor: colors.alert.DEFAULT } : {}}
              onClick={() => {
                onShowStartTimePickerChange(!showStartTimePicker)
                onShowCalendarChange(false)
                onShowEndTimePickerChange(false)
              }}
              title="Start Time"
            >
              <span
                className={
                  displayedStartTime ? 'text-primary-text' : 'text-silver-light opacity-60'
                }
              >
                {displayedStartTime || 'Start Time'}
              </span>
            </div>
            {showStartTimePicker && (
              <TimePicker
                timeSlots={validStartTimes}
                onTimeSelect={onStartTimeSelect}
                selectedTime={startTime}
                ref={startTimeRef}
              />
            )}
            {/* Time Error - positioned below start time picker */}
            {timeError && (
              <p
                className="text-xs mt-1.5 flex items-center gap-1 font-medium absolute top-full left-0 whitespace-nowrap"
                style={{ color: colors.alert.DEFAULT }}
              >
                <CircleAlert className="w-3.5 h-3.5 flex-shrink-0" color={colors.alert.DEFAULT} />
                {timeError}
              </p>
            )}
          </div>

          <span className="text-silver-light">–</span>

          <div className="w-[120px] relative" ref={endTimeContainerRef}>
            <div
              className={`w-full rounded-lg border px-3 py-2 text-sm text-center focus:border-primary focus:ring-primary focus:outline-none cursor-pointer shadow ${
                timeError ? 'border-alert' : 'border-primary-outline'
              }`}
              style={timeError ? { borderColor: colors.alert.DEFAULT } : {}}
              onClick={() => {
                onShowEndTimePickerChange(!showEndTimePicker)
                onShowCalendarChange(false)
                onShowStartTimePickerChange(false)
              }}
              title="End Time"
            >
              <span
                className={displayedEndTime ? 'text-primary-text' : 'text-silver-light opacity-60'}
              >
                {displayedEndTime || 'End Time'}
              </span>
            </div>
            {showEndTimePicker && (
              <TimePicker
                timeSlots={validEndTimes}
                onTimeSelect={onEndTimeSelect}
                selectedTime={endTime}
                ref={endTimeRef}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add margin when there's an error to make space for absolute positioned error */}
      {(dateError || timeError) && <div className="mb-3"></div>}
    </>
  )
}
